/**
 * @file BootPipeline.ts
 * @role Settles the boot work performed behind the live splash: retryable critical-bundle
 *       loading, restore-first RGS session creation, trust-boundary validation, and
 *       best-effort critical GPU preparation.
 * @owns Concurrent boot-task sequencing and the validated BootResult contract.
 * @reads Injected asset, RGS, diagnostics, renderer-preparation, and retry-surface ports.
 * @neverDoes Construct services or scenes. Initialize Pixi. Hide the boot shell.
 * @invariants 5 (RGS authority), 10 (validate before use), 21 (retryable terminal surface).
 * @see src/app/Bootstrapper.ts, src/app/BootRetry.ts, docs/BACKEND_CONTRACT.md
 */
import { loadWithRetry, usePreStartedAttempt, type RetrySurface } from './BootRetry';
import type { GameApplication } from './GameApplication';
import { CRITICAL_GPU_TEXTURE_IDS } from '@core/assets/AssetManifest';
import type { AssetBundleName } from '@core/assets/AssetBundleName';
import type { AssetService } from '@core/assets/AssetService';
import type { TextureResolver } from '@core/assets/TextureResolver';
import type { IRgsClient } from '@core/backend/IRgsClient';
import type { SpinResultDto } from '@core/backend/dto';
import { validateRestoreSession } from '@core/backend/validators';
import type { DiagnosticsService } from '@core/diagnostics/DiagnosticsService';
import { GameError } from '@core/errors/GameError';
import type { Logger } from '@core/logging/Logger';
import type { Texture } from 'pixi.js';

type ValidatedRestoreSession = ReturnType<typeof validateRestoreSession>;

export interface BootResult {
  session: ValidatedRestoreSession;
  recoveredRound?: SpinResultDto;
}

export interface BootPipelineDependencies {
  assets: Pick<AssetService, 'loadBundle'>;
  app: Pick<GameApplication, 'prepareTextures'>;
  resolveTexture: TextureResolver;
  rgsClient: Pick<IRgsClient, 'createSession' | 'restoreSession'>;
  reelShape: { columns: number; rows: number };
  symbolIds: readonly string[];
  logger: Logger;
  diagnostics: Pick<DiagnosticsService, 'record'>;
}

export interface BootPipelineRunOptions {
  retrySurface: RetrySurface;
  startupBundles: readonly AssetBundleName[];
}

export class BootPipeline {
  private attemptBootSession: (() => Promise<unknown>) | undefined;

  constructor(private readonly dependencies: BootPipelineDependencies) {}

  /**
   * Start the transport round-trip before splash loading. Rejection is observed immediately
   * to avoid an unhandled-rejection report while the promise is intentionally settling in
   * parallel; the same promise still rejects when consumed by run().
   */
  prestartSession(): void {
    if (this.attemptBootSession) return;
    const preStarted = this.restoreOrCreateSession();
    void preStarted.catch(() => undefined);
    this.attemptBootSession = usePreStartedAttempt(preStarted, () => this.restoreOrCreateSession());
  }

  async run(options: BootPipelineRunOptions): Promise<BootResult> {
    const { assets, app, resolveTexture, logger, diagnostics } = this.dependencies;
    this.prestartSession();

    await loadWithRetry(
      options.retrySurface,
      (code) => diagnostics.record('assets', code),
      { code: 'ASSET_BUNDLE_FAILED', message: 'Failed to load game assets' },
      async () => {
        await Promise.all(options.startupBundles.map((bundle) => assets.loadBundle(bundle)));
      },
    );

    // Texture preparation and session settlement overlap. GPU preparation is an optimization;
    // a failure records diagnostics and leaves normal first-render upload as the fallback.
    const gpuPreparation = this.prepareCriticalTextures(app, resolveTexture, logger, diagnostics);
    let session: ValidatedRestoreSession | undefined;
    const attemptBootSession = this.attemptBootSession;
    if (!attemptBootSession) {
      throw new GameError('SESSION_CREATE_FAILED', 'Boot session attempt was not initialized');
    }
    await loadWithRetry(
      options.retrySurface,
      (code) => diagnostics.record('rgs', code),
      { code: 'SESSION_CREATE_FAILED', message: 'Failed to connect to the game server' },
      async () => {
        session = validateRestoreSession(await attemptBootSession(), {
          columns: this.dependencies.reelShape.columns,
          rows: this.dependencies.reelShape.rows,
          symbolIds: this.dependencies.symbolIds,
        });
      },
    );
    if (!session) {
      throw new GameError('SESSION_CREATE_FAILED', 'Session unavailable after retry loop');
    }
    await gpuPreparation;

    const recoveredRound = session.hasOpenRound ? session.openRound : undefined;
    return {
      session,
      ...(recoveredRound ? { recoveredRound } : {}),
    };
  }

  private async restoreOrCreateSession(): Promise<unknown> {
    try {
      return await this.dependencies.rgsClient.restoreSession();
    } catch {
      return { ...(await this.dependencies.rgsClient.createSession()), hasOpenRound: false };
    }
  }

  private async prepareCriticalTextures(
    app: Pick<GameApplication, 'prepareTextures'>,
    resolveTexture: TextureResolver,
    logger: Logger,
    diagnostics: Pick<DiagnosticsService, 'record'>,
  ): Promise<void> {
    const textures = CRITICAL_GPU_TEXTURE_IDS.map(resolveTexture).filter(
      (texture): texture is Texture => texture !== undefined,
    );
    try {
      await app.prepareTextures(textures);
    } catch {
      logger.warn('[assets] critical GPU preparation failed; using first-render upload fallback');
      diagnostics.record('assets', 'GPU_PREPARE_FAILED');
    }
  }
}
